import { Platform } from '../types';

interface PlatformOption {
  id: Platform;
  label: string;
  icon: string;
  color: string;
  sub: string;
}

const PLATFORMS: PlatformOption[] = [
  { id: 'facebook_feed', label: 'Facebook Feed', icon: 'ri-facebook-circle-fill', color: 'text-blue-400', sub: '1080×1080 / 1200×628' },
  { id: 'instagram_feed', label: 'Instagram Feed', icon: 'ri-instagram-line', color: 'text-pink-400', sub: '1080×1080 / 1080×1350' },
  { id: 'instagram_story', label: 'Instagram Story / Reel', icon: 'ri-smartphone-line', color: 'text-fuchsia-400', sub: '1080×1920' },
  { id: 'meta_ad', label: 'Meta Ad', icon: 'ri-meta-line', color: 'text-cyan-400', sub: 'Facebook + Instagram' },
  { id: 'google_display', label: 'Google Display', icon: 'ri-google-fill', color: 'text-green-400', sub: '300×250 / 728×90 / 160×600' },
  { id: 'tiktok', label: 'TikTok', icon: 'ri-tiktok-line', color: 'text-red-400', sub: '1080×1920 / 9:16' },
  { id: 'linkedin', label: 'LinkedIn', icon: 'ri-linkedin-box-fill', color: 'text-sky-400', sub: '1200×627 / 1080×1080' },
];

interface PlatformSelectorProps {
  selected: Platform[];
  onChange: (platforms: Platform[]) => void;
}

export default function PlatformSelector({ selected, onChange }: PlatformSelectorProps) {
  const toggle = (id: Platform) => {
    onChange(
      selected.includes(id) ? selected.filter((p) => p !== id) : [...selected, id]
    );
  };

  const allSelected = selected.length === PLATFORMS.length;
  const toggleAll = () => onChange(allSelected ? [] : PLATFORMS.map((p) => p.id));

  return (
    <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-white">Choose Destination</h3>
          <div className="relative group">
            <div className="w-4 h-4 flex items-center justify-center cursor-default">
              <i className="ri-question-line text-stone-500 text-xs" />
            </div>
            <div className="absolute left-5 top-0 w-56 bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-stone-400 hidden group-hover:block z-10 leading-relaxed">
              Select the platforms you want to export assets for. Each platform may require different sizes.
            </div>
          </div>
        </div>
        <button
          onClick={toggleAll}
          className="text-xs text-orange-400 hover:text-orange-300 transition-colors cursor-pointer whitespace-nowrap"
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {PLATFORMS.map((p) => {
          const isActive = selected.includes(p.id);
          return (
            <button
              key={p.id}
              onClick={() => toggle(p.id)}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                isActive
                  ? 'bg-orange-500/10 border-orange-500/60 ring-1 ring-orange-500/30'
                  : 'bg-stone-800/40 border-stone-700/60 hover:border-stone-600'
              }`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${isActive ? 'bg-orange-500/20' : 'bg-stone-800'}`}>
                <i className={`${p.icon} text-base ${isActive ? 'text-orange-400' : p.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-semibold truncate ${isActive ? 'text-white' : 'text-stone-300'}`}>{p.label}</div>
                <div className="text-xs text-stone-600 truncate">{p.sub}</div>
              </div>
              <div className={`w-4 h-4 flex items-center justify-center rounded-full border shrink-0 ${
                isActive ? 'bg-orange-500 border-orange-500' : 'border-stone-600'
              }`}>
                {isActive && <i className="ri-check-line text-white text-xs" />}
              </div>
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="mt-3 flex items-center gap-1.5">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-checkbox-circle-fill text-orange-400 text-sm" />
          </div>
          <span className="text-xs text-stone-400">
            <span className="text-orange-400 font-semibold">{selected.length}</span> platform{selected.length !== 1 ? 's' : ''} selected
          </span>
        </div>
      )}
    </div>
  );
}
